import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, Baby, Hand, Apple, CalendarCheck, MessageCircle, Check } from "lucide-react";
import { CTABanner } from "../components/CTABanner";
import { SEO } from "../components/SEO";
import { SERVICES, JANE_APP_URL, WHATSAPP } from "../data/siteData";
import { trackEvent } from "../lib/analytics";

const spring = { type: "spring", stiffness: 200, damping: 20 };
const ICONS = { Activity, Baby, Hand, Apple, MessageCircle };
const ICON_STYLES = {
  sunny: "bg-sunny text-navy",
  teal: "bg-teal text-navy",
  butter: "bg-butter text-navy",
  lilac: "bg-lilac text-navy",
  leaf: "bg-leaf text-navy",
};
const BLOB_BG = { sunny: "bg-sunny-soft", teal: "bg-teal-soft", butter: "bg-butter-soft", lilac: "bg-lilac-soft", leaf: "bg-leaf-soft" };

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const Icon = ICONS[service.icon];

  return (
    <div data-testid={`service-page-${service.slug}`}>
      <SEO title={service.seoTitle} description={service.seoDescription} />

      <section className="relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-72 h-72 bg-butter-soft blob-shape opacity-70 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-14 grid lg:grid-cols-2 gap-12 items-center relative">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
            <span className={`inline-flex items-center gap-2.5 text-xs font-extrabold uppercase tracking-[0.16em] px-4 py-2 rounded-full ${ICON_STYLES[service.color]}`}>
              <Icon className="w-4 h-4" strokeWidth={2.5} />
              {service.tagline}
            </span>
            <h1 data-testid="service-hero-title" className="mt-6 font-display text-4xl sm:text-5xl font-semibold text-navy tracking-tight text-balance">
              {service.heroTitle}
            </h1>
            <p data-testid="service-hero-text" className="mt-5 text-base sm:text-lg text-navy-light leading-relaxed">
              {service.heroText}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              {service.slug === "speech-language-therapy" ? (
                <a
                  href={JANE_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("book_evaluation_click", { location: "service_hero", service: service.slug, channel: "jane" })}
                  data-testid="service-book-btn"
                  className="inline-flex items-center justify-center gap-2 bg-sunny text-navy font-extrabold px-8 py-4 rounded-full shadow-lg shadow-orange-500/25 transition-all duration-200 hover:-translate-y-1 hover:bg-sunny-dark focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2"
                >
                  <CalendarCheck className="w-5 h-5" strokeWidth={2.5} />
                  Book an Evaluation
                </a>
              ) : (
                <Link
                  to="/contact"
                  onClick={() => trackEvent("book_evaluation_click", { location: "service_hero", service: service.slug, channel: "contact" })}
                  data-testid="service-book-btn"
                  className="inline-flex items-center justify-center gap-2 bg-sunny text-navy font-extrabold px-8 py-4 rounded-full shadow-lg shadow-orange-500/25 transition-all duration-200 hover:-translate-y-1 hover:bg-sunny-dark focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2"
                >
                  <CalendarCheck className="w-5 h-5" strokeWidth={2.5} />
                  Book an Evaluation
                </Link>
              )}
              <a
                href={WHATSAPP.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("whatsapp_click", { location: "service_hero", service: service.slug })}
                data-testid="service-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 bg-white text-navy font-bold px-8 py-4 rounded-full border-2 border-border transition-all duration-200 hover:-translate-y-1 hover:border-teal hover:text-teal-dark"
              >
                <MessageCircle className="w-5 h-5" strokeWidth={2.25} />
                Ask a question first
              </a>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ ...spring, delay: 0.12 }} className="relative">
            <div className={`absolute -inset-4 ${BLOB_BG[service.color]} ${service.portrait ? "rounded-[3rem]" : "blob-shape"} opacity-70 pointer-events-none`} />
            <img
              src={service.image}
              alt={service.fullName}
              data-testid="service-hero-image"
              className={`relative ${service.portrait ? "rounded-[2.5rem] aspect-[4/5] max-w-md mx-auto" : "blob-shape-alt aspect-[4/3]"} w-full object-cover shadow-xl shadow-navy/10`}
              loading="lazy"
            />
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={spring}
          data-testid="service-reasons"
          className="bg-white rounded-[2rem] border border-border p-8 sm:p-10"
        >
          <h2 className="font-display text-xl sm:text-2xl font-semibold text-navy">Why families come to us</h2>
          <ul className="mt-6 space-y-3.5">
            {service.reasons.map((reason) => (
              <li key={reason} className="flex items-start gap-3 text-sm sm:text-base font-bold text-navy-light">
                <span className="w-5 h-5 rounded-full bg-sunny-soft flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-sunny-dark" strokeWidth={3} />
                </span>
                {reason}
              </li>
            ))}
          </ul>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ ...spring, delay: 0.08 }}
          data-testid="service-signs"
          className="bg-white rounded-[2rem] border border-border p-8 sm:p-10"
        >
          <h2 className="font-display text-xl sm:text-2xl font-semibold text-navy">Signs your child may benefit</h2>
          <ul className="mt-6 space-y-3.5">
            {service.signs.map((sign) => (
              <li key={sign} className="flex items-start gap-3 text-sm sm:text-base font-bold text-navy-light">
                <span className="w-5 h-5 rounded-full bg-teal-soft flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-teal-dark" strokeWidth={3} />
                </span>
                {sign}
              </li>
            ))}
          </ul>
        </motion.div>
      </section>

      <section className="bg-white border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={spring}>
            <h2 data-testid="service-evaluation-title" className="font-display text-2xl sm:text-3xl font-semibold text-navy tracking-tight">
              What to expect at an evaluation
            </h2>
            <p className="mt-3 text-base text-navy-light max-w-2xl">No pressure, no jargon - just a relaxed first visit focused on your child.</p>
          </motion.div>
          <div className="mt-10 grid sm:grid-cols-3 gap-6">
            {service.evaluation.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ ...spring, delay: i * 0.1 }}
                data-testid={`service-evaluation-step-${i}`}
                className="bg-cream rounded-[2rem] border border-border p-7"
              >
                <span className="font-display text-4xl font-semibold text-sunny">0{i + 1}</span>
                <h3 className="mt-3 font-display text-lg font-semibold text-navy">{step.title}</h3>
                <p className="mt-2 text-sm text-navy-light leading-relaxed">{step.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        title={`Wondering if ${service.name.toLowerCase()} could help your child?`}
        subtitle="The best way to know is a relaxed, play-based evaluation. Book a time that suits your family."
        service={service.slug}
        onlineBooking={service.slug === "speech-language-therapy"}
      />
    </div>
  );
}
